export type BoardImpactCategoryKey =
  | 'straight_flush'
  | 'quads'
  | 'full_house'
  | 'flush'
  | 'straight'
  | 'trips'
  | 'set'
  | 'two_pair'
  | 'overpair'
  | 'top_pair'
  | 'second_pair'
  | 'third_pair'
  | 'underpair'
  | 'ace_high'
  | 'broadway_high'
  | 'air';

export type BoardDrawCategoryKey =
  | 'flush_draw'
  | 'backdoor_flush_draw'
  | 'oesd'
  | 'gutshot'
  | 'two_overcards'
  | 'one_overcard';

export type BoardImpactBucket = {
  key: BoardImpactCategoryKey;
  label: string;
  combos: number;
  percentage: number;
  handClasses: string[];
};

export type BoardDrawBucket = {
  key: BoardDrawCategoryKey;
  label: string;
  combos: number;
  percentage: number;
  handClasses: string[];
};

export type BoardImpactResult = {
  totalCombos: number;
  buckets: BoardImpactBucket[];
  drawBuckets: BoardDrawBucket[];
};

export type BoardComboDiagnostic = {
  comboKey: string;
  handClass: string;
  madeHand: {
    key: BoardImpactCategoryKey;
    label: string;
  };
  draws: Array<{
    key: BoardDrawCategoryKey;
    label: string;
  }>;
};

type PokerSuit = 's' | 'h' | 'd' | 'c';

type PokerCard = {
  rank: string;
  suit: PokerSuit;
  token: string;
};

type PokerCombo = {
  handClass: string;
  cardA: PokerCard;
  cardB: PokerCard;
};

const SUITS: PokerSuit[] = ['s', 'h', 'd', 'c'];

const RANK_VALUE: Record<string, number> = {
  A: 14,
  K: 13,
  Q: 12,
  J: 11,
  T: 10,
  '9': 9,
  '8': 8,
  '7': 7,
  '6': 6,
  '5': 5,
  '4': 4,
  '3': 3,
  '2': 2,
};

const BUCKET_LABELS: Record<BoardImpactCategoryKey, string> = {
  straight_flush: 'Straight flush',
  quads: 'Quads',
  full_house: 'Full house',
  flush: 'Flush',
  straight: 'Straight',
  trips: 'Trips',
  set: 'Sets',
  two_pair: 'Two pair',
  overpair: 'Overpair',
  top_pair: 'Top pair',
  second_pair: 'Second pair',
  third_pair: 'Third pair',
  underpair: 'Underpair',
  ace_high: 'Ace high',
  broadway_high: 'Broadway high',
  air: 'Air',
};

const DRAW_BUCKET_LABELS: Record<BoardDrawCategoryKey, string> = {
  flush_draw: 'Flush draw',
  backdoor_flush_draw: 'Backdoor flush draw',
  oesd: 'OESD',
  gutshot: 'Gutshot',
  two_overcards: '2 overcards',
  one_overcard: '1 overcard',
};

const BUCKET_ORDER: BoardImpactCategoryKey[] = [
  'straight_flush',
  'quads',
  'full_house',
  'flush',
  'straight',
  'trips',
  'set',
  'two_pair',
  'overpair',
  'top_pair',
  'second_pair',
  'third_pair',
  'underpair',
  'ace_high',
  'broadway_high',
  'air',
];

const DRAW_BUCKET_ORDER: BoardDrawCategoryKey[] = [
  'flush_draw',
  'backdoor_flush_draw',
  'oesd',
  'gutshot',
  'two_overcards',
  'one_overcard',
];

const STRAIGHT_SEQUENCES: number[][] = [
  [14, 13, 12, 11, 10],
  [13, 12, 11, 10, 9],
  [12, 11, 10, 9, 8],
  [11, 10, 9, 8, 7],
  [10, 9, 8, 7, 6],
  [9, 8, 7, 6, 5],
  [8, 7, 6, 5, 4],
  [7, 6, 5, 4, 3],
  [6, 5, 4, 3, 2],
  [5, 4, 3, 2, 1],
];

function normalizeRank(value: string): string {
  return value.trim().toUpperCase();
}

function normalizeSuit(value: string): PokerSuit | null {
  const suit = value.trim().toLowerCase();

  if (suit === 's' || suit === '♠') return 's';
  if (suit === 'h' || suit === '♥') return 'h';
  if (suit === 'd' || suit === '♦') return 'd';
  if (suit === 'c' || suit === '♣') return 'c';

  return null;
}

export function parsePokerCard(value: string | null | undefined): PokerCard | null {
  const token = (value || '').trim();

  if (token.length !== 2) {
    return null;
  }

  const rank = normalizeRank(token[0]);
  const suit = normalizeSuit(token[1]);

  if (!RANK_VALUE[rank] || !suit) {
    return null;
  }

  return {
    rank,
    suit,
    token: `${rank}${suit}`,
  };
}

function buildCard(rank: string, suit: PokerSuit): PokerCard {
  return {
    rank,
    suit,
    token: `${rank}${suit}`,
  };
}

function getHandClassRanks(handClass: string): { highRank: string; lowRank: string } | null {
  const normalized = handClass.trim();

  if (normalized.length < 2) {
    return null;
  }

  const rankA = normalizeRank(normalized[0]);
  const rankB = normalizeRank(normalized[1]);

  if (!RANK_VALUE[rankA] || !RANK_VALUE[rankB]) {
    return null;
  }

  if (RANK_VALUE[rankA] >= RANK_VALUE[rankB]) {
    return {
      highRank: rankA,
      lowRank: rankB,
    };
  }

  return {
    highRank: rankB,
    lowRank: rankA,
  };
}

export function expandHandClassToCombos(handClass: string): PokerCombo[] {
  const normalized = handClass.trim();
  const ranks = getHandClassRanks(normalized);

  if (!ranks) {
    return [];
  }

  const { highRank, lowRank } = ranks;

  if (highRank === lowRank) {
    const combos: PokerCombo[] = [];

    for (let i = 0; i < SUITS.length; i += 1) {
      for (let j = i + 1; j < SUITS.length; j += 1) {
        combos.push({
          handClass: normalized,
          cardA: buildCard(highRank, SUITS[i]),
          cardB: buildCard(lowRank, SUITS[j]),
        });
      }
    }

    return combos;
  }

  const suffix = normalized.slice(2).toLowerCase();

  if (suffix === 's') {
    return SUITS.map((suit) => ({
      handClass: normalized,
      cardA: buildCard(highRank, suit),
      cardB: buildCard(lowRank, suit),
    }));
  }

  if (suffix === 'o') {
    const combos: PokerCombo[] = [];

    SUITS.forEach((highSuit) => {
      SUITS.forEach((lowSuit) => {
        if (highSuit === lowSuit) {
          return;
        }

        combos.push({
          handClass: normalized,
          cardA: buildCard(highRank, highSuit),
          cardB: buildCard(lowRank, lowSuit),
        });
      });
    });

    return combos;
  }

  return [];
}

function removeBoardBlockedCombos(combos: PokerCombo[], boardCards: PokerCard[]): PokerCombo[] {
  const blockedTokens = new Set(boardCards.map((card) => card.token));

  return combos.filter(
    (combo) => !blockedTokens.has(combo.cardA.token) && !blockedTokens.has(combo.cardB.token),
  );
}

function parseComboKey(value: string): PokerCombo | null {
  const normalized = value.trim();

  if (normalized.length !== 4) {
    return null;
  }

  const cardA = parsePokerCard(normalized.slice(0, 2));
  const cardB = parsePokerCard(normalized.slice(2, 4));

  if (!cardA || !cardB) {
    return null;
  }

  if (cardA.token === cardB.token) {
    return null;
  }

  const highRank =
    RANK_VALUE[cardA.rank] >= RANK_VALUE[cardB.rank] ? cardA.rank : cardB.rank;
  const lowRank =
    RANK_VALUE[cardA.rank] >= RANK_VALUE[cardB.rank] ? cardB.rank : cardA.rank;

  const handClass =
    highRank === lowRank
      ? `${highRank}${lowRank}`
      : cardA.suit === cardB.suit
        ? `${highRank}${lowRank}s`
        : `${highRank}${lowRank}o`;

  return {
    handClass,
    cardA,
    cardB,
  };
}

function countRanks(cards: PokerCard[]): Map<string, number> {
  const counts = new Map<string, number>();

  cards.forEach((card) => {
    counts.set(card.rank, (counts.get(card.rank) || 0) + 1);
  });

  return counts;
}

function countSuits(cards: PokerCard[]): Map<PokerSuit, number> {
  const counts = new Map<PokerSuit, number>();

  cards.forEach((card) => {
    counts.set(card.suit, (counts.get(card.suit) || 0) + 1);
  });

  return counts;
}

function getDistinctBoardRanksByStrength(boardCards: PokerCard[]): string[] {
  return Array.from(new Set(boardCards.map((card) => card.rank))).sort(
    (left, right) => RANK_VALUE[right] - RANK_VALUE[left],
  );
}

function getStraightRankValues(cards: PokerCard[]): Set<number> {
  const values = new Set<number>();

  cards.forEach((card) => {
    values.add(RANK_VALUE[card.rank]);

    if (card.rank === 'A') {
      values.add(1);
    }
  });

  return values;
}

function hasStraight(cards: PokerCard[]): boolean {
  const values = getStraightRankValues(cards);

  return STRAIGHT_SEQUENCES.some((sequence) =>
    sequence.every((value) => values.has(value)),
  );
}

function hasFlush(cards: PokerCard[]): boolean {
  return Array.from(countSuits(cards).values()).some((count) => count >= 5);
}

function hasStraightFlush(cards: PokerCard[]): boolean {
  return SUITS.some((suit) => {
    const suitedCards = cards.filter((card) => card.suit === suit);

    return suitedCards.length >= 5 && hasStraight(suitedCards);
  });
}

function getOpenEndedStraightDrawMissingRanks(cards: PokerCard[]): Set<number> {
  const values = getStraightRankValues(cards);
  const missing = new Set<number>();

  for (let start = 2; start <= 10; start += 1) {
    const run = [start, start + 1, start + 2, start + 3];

    if (!run.every((value) => values.has(value))) {
      continue;
    }

    const lowerOut = start - 1;
    const upperOut = start + 4;

    if (lowerOut >= 2) {
      missing.add(lowerOut);
    }

    if (upperOut <= 14) {
      missing.add(upperOut);
    }
  }

  return missing;
}

function getGutshotMissingRanks(cards: PokerCard[]): Set<number> {
  const values = getStraightRankValues(cards);
  const missing = new Set<number>();

  STRAIGHT_SEQUENCES.forEach((sequence) => {
    const missingValues = sequence.filter((value) => !values.has(value));

    if (missingValues.length === 1) {
      missing.add(missingValues[0]);
    }
  });

  return missing;
}

function classifyComboAgainstBoard(
  combo: PokerCombo,
  boardCards: PokerCard[],
): BoardImpactCategoryKey {
  const handCards = [combo.cardA, combo.cardB];
  const allCards = [...handCards, ...boardCards];

  const allRankCounts = countRanks(allCards);
  const boardRankCounts = countRanks(boardCards);
  const boardRanksByStrength = getDistinctBoardRanksByStrength(boardCards);
  const rankFrequencies = Array.from(allRankCounts.values()).sort((left, right) => right - left);

  if (hasStraightFlush(allCards)) {
    return 'straight_flush';
  }

  if (rankFrequencies[0] === 4) {
    return 'quads';
  }

  const hasTripsOrBetter = rankFrequencies[0] === 3;
  const hasPairOrBetter = rankFrequencies.some((count) => count >= 2);

  if (hasTripsOrBetter && rankFrequencies[1] >= 2) {
    return 'full_house';
  }

  if (hasFlush(allCards)) {
    return 'flush';
  }

  if (hasStraight(allCards)) {
    return 'straight';
  }

  const isPocketPair = combo.cardA.rank === combo.cardB.rank;
  const pocketPairRank = isPocketPair ? combo.cardA.rank : null;

  if (isPocketPair && pocketPairRank && boardRankCounts.has(pocketPairRank)) {
    return 'set';
  }

  if (hasTripsOrBetter) {
    return 'trips';
  }

  const pairRanks = Array.from(allRankCounts.entries())
    .filter(([, count]) => count === 2)
    .map(([rank]) => rank);

  if (pairRanks.length >= 2) {
    return 'two_pair';
  }

  const boardHighRank = boardRanksByStrength[0];
  const boardHighValue = RANK_VALUE[boardHighRank];

  if (isPocketPair && pocketPairRank) {
    const pocketValue = RANK_VALUE[pocketPairRank];

    if (pocketValue > boardHighValue) {
      return 'overpair';
    }

    return 'underpair';
  }

  const pairedBoardRanks = handCards
    .map((card) => card.rank)
    .filter((rank) => boardRankCounts.has(rank));

  if (pairedBoardRanks.length > 0) {
    const bestPairedRank = pairedBoardRanks.sort(
      (left, right) => RANK_VALUE[right] - RANK_VALUE[left],
    )[0];

    const boardRankIndex = boardRanksByStrength.indexOf(bestPairedRank);

    if (boardRankIndex === 0) {
      return 'top_pair';
    }

    if (boardRankIndex === 1) {
      return 'second_pair';
    }

    return 'third_pair';
  }

  const highestHandRankValue = Math.max(RANK_VALUE[combo.cardA.rank], RANK_VALUE[combo.cardB.rank]);

  if (highestHandRankValue === RANK_VALUE.A) {
    return 'ace_high';
  }

  if (highestHandRankValue >= RANK_VALUE.T) {
    return 'broadway_high';
  }

  if (!hasPairOrBetter) {
    return 'air';
  }

  return 'air';
}

function classifyDrawsAgainstBoard(
  combo: PokerCombo,
  boardCards: PokerCard[],
  madeHandCategory: BoardImpactCategoryKey,
): BoardDrawCategoryKey[] {
  const handCards = [combo.cardA, combo.cardB];
  const allCards = [...handCards, ...boardCards];
  const draws = new Set<BoardDrawCategoryKey>();
  const boardLength = boardCards.length;

  const madeFlushOrBetter = madeHandCategory === 'flush' || madeHandCategory === 'straight_flush';
  const madeStraightOrBetter =
    madeHandCategory === 'straight' || madeHandCategory === 'straight_flush';

  if (boardLength < 5 && !madeFlushOrBetter) {
    const allSuitCounts = countSuits(allCards);
    const handSuitCounts = countSuits(handCards);

    SUITS.forEach((suit) => {
      const allSuitCount = allSuitCounts.get(suit) || 0;
      const handSuitCount = handSuitCounts.get(suit) || 0;

      if (handSuitCount <= 0) {
        return;
      }

      if (allSuitCount === 4) {
        draws.add('flush_draw');
        return;
      }

      if (boardLength === 3 && allSuitCount === 3) {
        draws.add('backdoor_flush_draw');
      }
    });
  }

  if (boardLength < 5 && !madeStraightOrBetter) {
    const oesdMissingRanks = getOpenEndedStraightDrawMissingRanks(allCards);
    const gutshotMissingRanks = getGutshotMissingRanks(allCards);

    if (oesdMissingRanks.size > 0) {
      draws.add('oesd');

      oesdMissingRanks.forEach((rank) => {
        gutshotMissingRanks.delete(rank);
      });
    }

    if (gutshotMissingRanks.size > 0) {
      draws.add('gutshot');
    }
  }

  if (
    madeHandCategory === 'ace_high' ||
    madeHandCategory === 'broadway_high' ||
    madeHandCategory === 'air'
  ) {
    const boardHighValue = Math.max(...boardCards.map((card) => RANK_VALUE[card.rank]));
    const overcardCount = handCards.filter((card) => RANK_VALUE[card.rank] > boardHighValue).length;

    if (overcardCount >= 2) {
      draws.add('two_overcards');
    } else if (overcardCount === 1) {
      draws.add('one_overcard');
    }
  }

  return Array.from(draws);
}

function buildEmptyCounts(): Record<BoardImpactCategoryKey, number> {
  return BUCKET_ORDER.reduce(
    (acc, key) => ({
      ...acc,
      [key]: 0,
    }),
    {} as Record<BoardImpactCategoryKey, number>,
  );
}

function buildEmptyDrawCounts(): Record<BoardDrawCategoryKey, number> {
  return DRAW_BUCKET_ORDER.reduce(
    (acc, key) => ({
      ...acc,
      [key]: 0,
    }),
    {} as Record<BoardDrawCategoryKey, number>,
  );
}

function buildEmptyHandClassSets(): Record<BoardImpactCategoryKey, Set<string>> {
  return BUCKET_ORDER.reduce(
    (acc, key) => ({
      ...acc,
      [key]: new Set<string>(),
    }),
    {} as Record<BoardImpactCategoryKey, Set<string>>,
  );
}

function buildEmptyDrawHandClassSets(): Record<BoardDrawCategoryKey, Set<string>> {
  return DRAW_BUCKET_ORDER.reduce(
    (acc, key) => ({
      ...acc,
      [key]: new Set<string>(),
    }),
    {} as Record<BoardDrawCategoryKey, Set<string>>,
  );
}

function buildEmptyResult(): BoardImpactResult {
  return {
    totalCombos: 0,
    buckets: BUCKET_ORDER.map((key) => ({
      key,
      label: BUCKET_LABELS[key],
      combos: 0,
      percentage: 0,
      handClasses: [],
    })),
    drawBuckets: DRAW_BUCKET_ORDER.map((key) => ({
      key,
      label: DRAW_BUCKET_LABELS[key],
      combos: 0,
      percentage: 0,
      handClasses: [],
    })),
  };
}

function buildResultFromCombos(liveCombos: PokerCombo[]): BoardImpactResult {
  const counts = buildEmptyCounts();
  const drawCounts = buildEmptyDrawCounts();
  const handClassesByBucket = buildEmptyHandClassSets();
  const handClassesByDrawBucket = buildEmptyDrawHandClassSets();

  liveCombos.forEach((combo) => {
    const boardCards = liveComboBoardCardsContext.get(combo) || [];
    const category = classifyComboAgainstBoard(combo, boardCards);
    counts[category] += 1;
    handClassesByBucket[category].add(combo.handClass);

    const draws = classifyDrawsAgainstBoard(combo, boardCards, category);

    draws.forEach((drawKey) => {
      drawCounts[drawKey] += 1;
      handClassesByDrawBucket[drawKey].add(combo.handClass);
    });
  });

  const totalCombos = BUCKET_ORDER.reduce((acc, key) => acc + counts[key], 0);

  return {
    totalCombos,
    buckets: BUCKET_ORDER.map((key) => ({
      key,
      label: BUCKET_LABELS[key],
      combos: counts[key],
      percentage: totalCombos > 0 ? (counts[key] / totalCombos) * 100 : 0,
      handClasses: Array.from(handClassesByBucket[key]),
    })),
    drawBuckets: DRAW_BUCKET_ORDER.map((key) => ({
      key,
      label: DRAW_BUCKET_LABELS[key],
      combos: drawCounts[key],
      percentage: totalCombos > 0 ? (drawCounts[key] / totalCombos) * 100 : 0,
      handClasses: Array.from(handClassesByDrawBucket[key]),
    })),
  };
}

const liveComboBoardCardsContext = new WeakMap<PokerCombo, PokerCard[]>();

function attachBoardContextToCombos(combos: PokerCombo[], boardCards: PokerCard[]): PokerCombo[] {
  combos.forEach((combo) => {
    liveComboBoardCardsContext.set(combo, boardCards);
  });

  return combos;
}

export function calculateBoardImpact({
  handClasses,
  boardCards,
}: {
  handClasses: string[];
  boardCards: Array<string | null | undefined>;
}): BoardImpactResult {
  const parsedBoardCards = boardCards
    .map((card) => parsePokerCard(card))
    .filter((card): card is PokerCard => Boolean(card));

  if (parsedBoardCards.length < 3) {
    return buildEmptyResult();
  }

  const liveCombos = handClasses.flatMap((handClass) =>
    removeBoardBlockedCombos(expandHandClassToCombos(handClass), parsedBoardCards),
  );

  return buildResultFromCombos(attachBoardContextToCombos(liveCombos, parsedBoardCards));
}

export function calculateBoardImpactFromComboKeys({
  comboKeys,
  boardCards,
}: {
  comboKeys: string[];
  boardCards: Array<string | null | undefined>;
}): BoardImpactResult {
  const parsedBoardCards = boardCards
    .map((card) => parsePokerCard(card))
    .filter((card): card is PokerCard => Boolean(card));

  if (parsedBoardCards.length < 3) {
    return buildEmptyResult();
  }

  const seenComboKeys = new Set<string>();
  const parsedCombos: PokerCombo[] = [];

  comboKeys.forEach((comboKey) => {
    const normalized = comboKey.trim();

    if (seenComboKeys.has(normalized)) {
      return;
    }

    seenComboKeys.add(normalized);

    const combo = parseComboKey(normalized);

    if (combo) {
      parsedCombos.push(combo);
    }
  });

  const liveCombos = removeBoardBlockedCombos(parsedCombos, parsedBoardCards);

  return buildResultFromCombos(attachBoardContextToCombos(liveCombos, parsedBoardCards));
}

function buildComboKey(combo: PokerCombo): string {
  return `${combo.cardA.token}${combo.cardB.token}`;
}

export function calculateBoardComboDiagnosticsFromComboKeys({
  comboKeys,
  boardCards,
}: {
  comboKeys: string[];
  boardCards: Array<string | null | undefined>;
}): BoardComboDiagnostic[] {
  const parsedBoardCards = boardCards
    .map((card) => parsePokerCard(card))
    .filter((card): card is PokerCard => Boolean(card));

  if (parsedBoardCards.length < 3) {
    return [];
  }

  const seenComboKeys = new Set<string>();
  const parsedCombos: PokerCombo[] = [];

  comboKeys.forEach((comboKey) => {
    const normalized = comboKey.trim();

    if (seenComboKeys.has(normalized)) {
      return;
    }

    seenComboKeys.add(normalized);

    const combo = parseComboKey(normalized);

    if (combo) {
      parsedCombos.push(combo);
    }
  });

  const liveCombos = removeBoardBlockedCombos(parsedCombos, parsedBoardCards);

  return liveCombos.map((combo) => {
    const madeHandKey = classifyComboAgainstBoard(combo, parsedBoardCards);
    const drawKeys = classifyDrawsAgainstBoard(combo, parsedBoardCards, madeHandKey);

    return {
      comboKey: buildComboKey(combo),
      handClass: combo.handClass,
      madeHand: {
        key: madeHandKey,
        label: BUCKET_LABELS[madeHandKey],
      },
      draws: drawKeys.map((drawKey) => ({
        key: drawKey,
        label: DRAW_BUCKET_LABELS[drawKey],
      })),
    };
  });
}